import { FormControl, Grid, InputLabel, MenuItem, Select, Theme, Typography } from '@mui/material';
import { FC, useCallback, useState } from 'react';

import { SequencingTier, SetShowSnackbar } from '../../../types';
import { EditorModal } from '../../modals';

type DeleteBaseTierProps = {
	theme: Theme;
	tiers: SequencingTier[];
	setShowSnackbar: SetShowSnackbar;
	handleClose: () => void;
	handleDelete: (tierId: number) => Promise<void>;
};

export const DeleteBaseTierModal: FC<DeleteBaseTierProps> = ({
	theme,
	tiers,
	setShowSnackbar,
	handleClose,
	handleDelete,
}) => {
	const [selectedTierId, setSelectedTierId] = useState<number>(null);

	const handleOnChange = useCallback(
		(tierId: number) => {
			setSelectedTierId(tierId);
		},
		[setSelectedTierId],
	);

	const onConfirm = useCallback(async () => {
		if (!selectedTierId) {
			setShowSnackbar({
				message: 'No rate/sublot selected',
				severity: 'error',
				autoHideDuration: 5000,
			});

			return;
		}

		handleDelete(selectedTierId);
	}, [selectedTierId, handleDelete, setShowSnackbar]);

	return (
		<EditorModal
			handleClose={handleClose}
			handleSave={onConfirm}
			headerText='Remove Base Tier'
			headerBorder
			loading={false}
			saveDisabled={!selectedTierId}
			theme={theme}
		>
			<Grid
				container
				spacing={2}
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignSelf: 'center',
					mt: 2,
					mb: 2,
				}}
			>
				<Grid item xs={12} mb={2}>
					<Typography variant='body1'>
						Select a rate/sublot combination to remove from the base tier.
					</Typography>
				</Grid>
				<Grid item xs={6}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel id='base-tier-label'>Rate/Sublot</InputLabel>
						<Select
							fullWidth
							labelId='base-tier-select-label'
							id='base-tier-select'
							value={selectedTierId}
							label='Rate/Sublot'
							disabled={false}
							onChange={(e) => handleOnChange(Number(e.target.value))}
						>
							{tiers.map((tier) => (
								<MenuItem key={tier.id} value={tier.id}>
									{`${tier.rateName}/${tier.sublotName}`}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
		</EditorModal>
	);
};
